import React, { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";

// --- Types ---
type Option = {
  id: string | number;
  label: string;
  value: string | number;
};

// --- Component ---
type SimpleDropdownProps = {
  options: Option[];
  value: Option | null;
  onChange: (opt: Option | null) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
  noResultsText?: string;
};

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.333a1 1 0 0 1-1.445 0L3.29 9.983a1 1 0 1 1 1.42-1.407l3.017 3.05 6.53-6.6a1 1 0 0 1 1.447.264z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.25 8.27a.75.75 0 0 1-.02-1.06z" />
    </svg>
  );
}

export function SimpleDropdown({
  options,
  value,
  onChange,
  placeholder = "Seçiniz...",
  disabled = false,
  label,
  className = "",
  noResultsText = "Seçenek bulunamadı",
}: SimpleDropdownProps) {
  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      ) : null}

      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400">
            <span className={`block truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
              {value ? value.label : placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronIcon className="h-4 w-4 text-gray-400" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white p-1 text-sm shadow-xl focus:outline-none">
              {options.length === 0 ? (
                <div className="p-3 text-gray-500">{noResultsText}</div>
              ) : (
                options.map((opt) => (
                  <Listbox.Option
                    key={opt.id}
                    value={opt}
                    className={({ active }) =>
                      `relative cursor-pointer select-none rounded-lg px-3 py-2 ${
                        active ? "bg-gray-100" : ""
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {opt.label}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-[#9B51E0]">
                            <CheckIcon className="h-4 w-4" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
}
